import { answerFeedback } from "../fuxi/yufa-answer-feedback.js";
import type { PracticePlan, HelpAction } from "../fuxi/yufa-practice.js";
import { YufaListening } from "./lianxi-yufa-listening.js";
import { showHelp, nextHelp } from "./lianxi-yufa-help.js";
import { optionsOf, readAnswer, yufaPart } from "./lianxi-yufa-item.js";
import { showPlaced } from "./lianxi-yufa-placing.js";
import { focusAnswer, resetItem, showOnly } from "./lianxi-yufa-item-view.js";
import { type YufaKeeper, settleAfterAnswer } from "./lianxi-yufa-keeper.js";
import { type KeptQueue, ItemQueue } from "./lianxi-yufa-queue.js";
import { markEmpty, markShown } from "./lianxi-yufa-said.js";
import { showFinished, showProgress } from "./lianxi-yufa-lines.js";

/**
 * A grammar exercise 语法练习: a few of an article's 例句 with the point's
 * word to fill back in and the words to put in order, one item at a time
 * (#964).
 *
 * `/lianxi/yufa/<article>/<n>/` is one, and `partials/lianxi/yufa.html`
 * draws its block, every item in it as a form, hidden until this shows it.
 * The items are asked in the exercise's order, an item missed comes back
 * once the others waiting have been asked (`./lianxi-yufa-queue.ts`), and
 * the exercise finishes after the initial questions and their bounded extra practice.
 * Each answer is marked by the grader a 复习 card is marked by
 * (`src/fuxi/yufa-marking.ts`), and the sentence is then shown whole with its
 * reading and its English, linked to its page and to the article.
 *
 * **A visitor stops at the taste.** The block names how many items, from
 * the first, a reader without an account answers
 * (`src/content/lianxi/yufa-taste.ts`). The first time the queue reaches an
 * item past them, the gate asking the reader to sign up is shown in its
 * place. An item missed inside the taste goes to the back of the queue as
 * ever, before the items the gate holds back.
 *
 * **The page keeps nothing, and a keeper may.** On the public page a reader
 * who leaves starts again. On `/user/lianxi/`, the handler's page for the
 * same exercise, the block starts the queue where the handler kept it, and a
 * {@link YufaKeeper} is told each answer so the handler can mark and keep
 * it. The keeper answers with the queue as kept, which the exercise adopts
 * over its own arithmetic, so two tabs on one exercise settle on what the
 * handler holds. The page marks each answer itself first, so the reader is
 * never waiting on the handler. `lianxi-yufa-recorder.ts` is the keeper.
 */

/** What the Exercise is built from: the block's pieces, by their names. */
export interface YufaExerciseProperties {
  /** The items, in the exercise's order. */
  readonly items: readonly HTMLFormElement[];

  /** The line over the items saying where the reader is. */
  readonly progress: HTMLElement;

  /** What is shown once all scheduled attempts are complete, and its line. */
  readonly finished: HTMLElement;
  readonly finishedLine: HTMLElement;

  /** Where the reader is, as the handler kept it. The beginning where absent. */
  readonly kept?: KeptQueue | undefined;
  readonly plan?: PracticePlan | undefined;
  readonly taste?: number | undefined;

  /** What keeps the reader's place, where anything does. */
  readonly keeper?: YufaKeeper | undefined;

  /**
   * Asked before an item is shown, and true where the gate a visitor meets
   * has taken its place. Absent for a reader with an account.
   */
  readonly gate?: ((at: number) => boolean) | undefined;
}

/** One exercise, on one block, over its items. */
export class YufaExercise {
  readonly #properties: YufaExerciseProperties;
  readonly #queue: ItemQueue;

  /** Whether the current item has been answered and awaits Next. */
  #answered = false;
  #attempt = 0;
  readonly #listening = new Map<HTMLFormElement, YufaListening>();

  constructor(properties: YufaExerciseProperties) {
    this.#properties = properties;
    this.#queue = new ItemQueue(
      properties.items.length,
      properties.kept,
      properties.plan,
      properties.taste,
    );

    for (const form of properties.items) {
      if (yufaPart(form, "listen") !== undefined) {
        this.#listening.set(
          form,
          new YufaListening(form, (action) => {
            this.listened(form, action);
          }),
        );
      }
      yufaPart(form, "help")?.addEventListener("click", () => {
        this.help(form);
      });
      form.addEventListener("submit", (event: Event) => {
        event.preventDefault();
        this.check(form);
      });
    }
  }

  /** Show the first item. */
  start(): void {
    this.#moveOn();
  }

  /**
   * Choose an option of the item showing, and mark it at once, right or
   * wrong, as a reading Exercise's question is marked (CB-23, CB-130). An
   * item of options therefore has no Check. Marking only a right option at
   * once told the reader that a choice left unmarked was wrong, and let them
   * try another.
   * @param form - The item.
   * @param option - The option's place in the order drawn.
   * @returns The radio button chosen, or nothing where none could be.
   */
  choose(form: HTMLFormElement, option: number): HTMLInputElement | undefined {
    const at = this.#queue.current();
    const radio = optionsOf(form)[option];

    if (
      at === undefined ||
      this.#properties.items[at] !== form ||
      this.#answered ||
      radio === undefined ||
      radio.disabled ||
      this.#listening.get(form)?.ready() === false
    ) {
      return undefined;
    }

    radio.checked = true;
    showPlaced(form);
    this.check(form);

    return radio;
  }

  /** Mark what the reader answered on an item, where it is the current one. */
  check(form: HTMLFormElement): void {
    const at = this.#queue.current();

    if (
      at === undefined ||
      this.#properties.items[at] !== form ||
      this.#answered ||
      this.#listening.get(form)?.ready() === false
    ) {
      return;
    }

    const answer = readAnswer(form);

    if (answer === undefined) {
      markEmpty(form);

      return;
    }

    const help = this.#queue.help();
    const passed = markShown(form, answer);
    this.#listening.get(form)?.show(help);

    this.#answered = true;

    const known = answerFeedback(answer);
    this.#queue.answered(
      passed,
      known === undefined ? {} : { answerId: known.id, status: known.status },
    );
    const button = yufaPart(form, "help");
    if (button !== undefined) {
      button.hidden = true;
    }
    const followup = yufaPart(form, "followup");
    if (followup !== undefined) {
      const exhausted = this.#queue.practice()?.exhausted.includes(at);
      const next = this.#queue.practice()?.extra.at(-1);
      followup.textContent = passed
        ? ""
        : exhausted === true
          ? "Practice for this question is finished. You can revisit the source section or repeat the exercise."
          : next !== at
            ? "A fresh question on this point will follow the other questions."
            : "You can try this question once more after the other questions.";
    }
    this.#showProgress();
    const attempt = ++this.#attempt;
    settleAfterAnswer(
      this.#properties.keeper?.answered(at, answer, help),
      (kept) => {
        if (attempt === this.#attempt) {
          this.#adopt(kept);
        }
      },
    );
  }

  /** Reveal one reviewed clue without submitting the answer. */
  help(form: HTMLFormElement): void {
    const item = this.#queue.current();
    if (
      item === undefined ||
      this.#properties.items[item] !== form ||
      this.#answered
    ) {
      return;
    }
    const { help, action } = nextHelp(form, this.#queue.help());
    this.#queue.assisted(help);
    showHelp(form, help);
    this.#listening.get(form)?.show(help);
    if (help.shown === true) {
      const why = yufaPart(form, "why");
      why?.setAttribute("tabindex", "-1");
      why?.focus();
    }
    const attempt = ++this.#attempt;
    settleAfterAnswer(
      this.#properties.keeper?.helped?.(item, action),
      (kept) => {
        if (attempt === this.#attempt) {
          this.#adopt(kept);
        }
      },
    );
  }

  /** Keep playback completion and text exposure under the same revision as other help. */
  private listened(form: HTMLFormElement, action: HelpAction): void {
    const item = this.#queue.current();
    if (
      item === undefined ||
      this.#properties.items[item] !== form ||
      this.#answered
    ) {
      return;
    }
    const help = { ...this.#queue.help(), [action]: true };
    this.#queue.assisted(help);
    this.#listening.get(form)?.show(help);
    const attempt = ++this.#attempt;
    settleAfterAnswer(
      this.#properties.keeper?.helped?.(item, action),
      (kept) => {
        if (attempt === this.#attempt) {
          this.#adopt(kept);
        }
      },
    );
  }

  /** The reader pressed Next: the next item, or the finish. */
  next(): void {
    if (this.#answered) {
      this.#answered = false;
      this.#moveOn();
    }
  }

  /** The exercise started again from the first item. */
  again(): void {
    this.#attempt += 1;
    const attempt = this.#attempt;
    this.#queue.reset();
    this.#answered = false;
    this.#properties.finished.hidden = true;
    this.#moveOn();
    void this.#properties.keeper?.again().then((kept) => {
      if (kept !== undefined && attempt === this.#attempt) {
        this.#adopt(kept);
      }
    });
  }

  /**
   * Adopt the queue the keeper answered.
   *
   * The head may have moved under an item the reader has not answered yet,
   * where another tab answered it, and that item is then left for the one
   * the keeper names. An item already answered waits for Next as before.
   */
  #adopt(kept: KeptQueue): void {
    this.#queue.adopt(kept);
    this.#showProgress();
    const current = this.#queue.current();
    if (!this.#answered && current !== undefined) {
      showHelp(this.#properties.items[current]!, this.#queue.help());
      this.#listening
        .get(this.#properties.items[current]!)
        ?.show(this.#queue.help());
    }

    const showing = this.#properties.items.findIndex(
      (form) => form.hidden === false,
    );

    if (
      !this.#answered &&
      this.#queue.current() !== (showing < 0 ? undefined : showing)
    ) {
      this.#moveOn();
    } else {
      this.#sayHandedOver();
    }
  }

  /** Show the item the queue names, or the finish, or the gate. */
  #moveOn(): void {
    for (const listening of this.#listening.values()) {
      listening.stop();
    }
    const at = this.#queue.current();
    const gated = at !== undefined && this.#properties.gate?.(at) === true;

    showOnly(this.#properties.items, gated ? undefined : at);

    if (gated) {
      return;
    }

    if (at === undefined) {
      this.#finish();

      return;
    }

    const form = this.#properties.items[at]!;

    resetItem(form);
    showHelp(form, this.#queue.help());
    this.#listening.get(form)?.show(this.#queue.help());
    focusAnswer(form);
    this.#listening.get(form)?.focus();
    this.#showProgress();
  }

  /** Say every item is answered, and offer the exercise again. */
  #finish(): void {
    this.#properties.finished.hidden = false;
    this.#sayHandedOver();
    this.#showProgress();
  }

  /** The finish line, saying the sentences are in 复习 once the keeper has heard so. */
  #sayHandedOver(): void {
    showFinished(this.#properties, this.#queue);
  }

  /** Say where the reader is, over the items. */
  #showProgress(): void {
    showProgress(this.#properties, this.#queue);
  }
}
